import { useParams } from 'react-router-dom';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IPermissions } from 'common/permissions.types';
import { permissionsUtil } from 'utils/permissionsUtil';

import { IntegrationSettingsData } from 'components/Settings/Settings.types';

import { ELocalStorageKeys, EQueryKeys } from '../constants/enums';
import useAxios from '../hooks/useAxios';
import { localStorageUtil } from '../utils/localStorageUtil';

import {
  EApiRoutes,
  fetchConfig,
  getApiRouteOrMock,
  getCustomHeader
} from './api.utils';

export default function useIntegration(currentPublisherId?: string) {
  const axios = useAxios();
  const queryClient = useQueryClient();
  const { portalId } = useParams();

  const canAccessIntegration = currentPublisherId
    ? !!permissionsUtil.canAccessIntegration(
        localStorageUtil.get<IPermissions>(ELocalStorageKeys.PERMISSIONS) || {},
        currentPublisherId
      )
    : !!portalId;

  const getIntegration = useQuery<IntegrationSettingsData, Error>({
    ...fetchConfig.general,
    queryKey: [EQueryKeys.INTEGRATION],
    queryFn: async () => {
      return await axios.get(
        getApiRouteOrMock(EApiRoutes.INTEGRATION),
        {},
        getCustomHeader(currentPublisherId, portalId)
      );
    },
    enabled: canAccessIntegration
  });

  const getPublisherEventsV1 = useQuery<IntegrationSettingsData, Error>({
    ...fetchConfig.general,
    queryKey: ['publisher-events', 'v1', currentPublisherId, portalId],
    queryFn: async () => {
      return await axios.get(
        `${getApiRouteOrMock(EApiRoutes.INTEGRATION)}/publisher-webhook-events-name?version=v1`,
        {},
        getCustomHeader(currentPublisherId, portalId)
      );
    },
    enabled: canAccessIntegration
  });

  const getPublisherEventsV2 = useQuery<IntegrationSettingsData, Error>({
    ...fetchConfig.general,
    queryKey: ['publisher-events', 'v2', currentPublisherId, portalId],
    queryFn: async () => {
      return await axios.get(
        `${getApiRouteOrMock(EApiRoutes.INTEGRATION)}/publisher-webhook-events-name?version=v2`,
        {},
        getCustomHeader(currentPublisherId, portalId)
      );
    },
    enabled: canAccessIntegration
  });

  const updateIntegration = useMutation(
    async (data: Partial<IntegrationSettingsData>) => {
      return await axios.put(
        getApiRouteOrMock(EApiRoutes.INTEGRATION),
        data,
        getCustomHeader(currentPublisherId, portalId)
      );
    },
    {
      onSuccess: (updatedIntegrationData) => {
        queryClient.setQueryData(
          [EQueryKeys.INTEGRATION],
          (oldIntegrationData: any) => ({
            ...oldIntegrationData,
            ...updatedIntegrationData
          })
        );
      }
    }
  );

  return {
    getIntegration,
    updateIntegration,
    getPublisherEventsV1,
    getPublisherEventsV2
  };
}
